// ============================================================
// diagnostics/report - the buffer as text a person can paste.
//
// Plain text on purpose: it goes into a chat message or an issue, and
// the reader is whoever is fixing the bug. The stamp comes first
// because "which build, which backend, which role" is the question
// every report used to cost a round trip to answer.
// ============================================================
import type { DiagContext, DiagEntry } from './log';

/** What another seat at the table last uploaded of its own log (v8.362.0). */
interface SeatDiag {
  name: string;
  role: 'GM' | 'player';
  online: boolean;
  /** The seat's own lines, as `formatSeatDiag` made them; empty when it never uploaded. */
  diag: string;
  /** When it uploaded, epoch millis. */
  diagAt?: number | null;
}

export interface BugReportInput {
  context: DiagContext;
  entries: DiagEntry[];
  env: { userAgent: string; url: string; now: number };
  /** Newest lines kept. */
  maxEntries?: number;
  /** The other seats' uploaded lines - the GM's report holds the whole table (v8.362.0). */
  table?: SeatDiag[];
}

/** The newest lines kept when a seat uploads its log - the size is what the players collection carries. */
const SEAT_DIAG_MAX_ENTRIES = 80;

/**
 * A seat's own lines as the text it uploads (v8.362.0). The same line
 * format as the report, so a reader sees one grammar; INFO lines are
 * kept - the twenty seconds before a drop are mostly INFO.
 */
export function formatSeatDiag(entries: DiagEntry[], max = SEAT_DIAG_MAX_ENTRIES): string {
  return entries.slice(-max).map(line).join('\n');
}

const DEFAULT_MAX_ENTRIES = 200;

const iso = (ms: number) => new Date(ms).toISOString();

function line(entry: DiagEntry): string {
  const level = entry.level.toUpperCase().padEnd(5);
  const data = entry.data === undefined ? '' : ' ' + safeJson(entry.data);
  return `${iso(entry.t)} ${level} ${entry.tag}: ${entry.msg}${data}`;
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** The stamp: which build, which backend, which role - first, because every report used to cost a round trip to ask. */
function stampLines(c: DiagContext, env: BugReportInput['env']): string[] {
  const role = c.sessionId ? (c.isGm ? 'GM' : 'player') : 'not in a session';
  return [
    '=== DEATHWATCH BUG REPORT ===',
    `version: ${c.version ?? 'unknown'}`,
    `built: ${c.buildTime ?? 'unknown'}`,
    `backend: ${c.backend ?? 'unknown'}`,
    `session: ${c.sessionId ?? '-'}`,
    `role: ${role}`,
    `player: ${c.playerId ?? '-'}`,
    `user: ${c.uid ?? '-'}`,
    ...(c.houseRules?.length ? [`house rules: ${c.houseRules.join(', ')}`] : []),
    `browser: ${env.userAgent}`,
    `url: ${env.url}`,
    `copied: ${iso(env.now)}`,
  ];
}

/** A seat in the report: one person's, or several silent offline seats of one person folded into one (`seats`). */
type ReportSeat = SeatDiag & { seats: number };

/**
 * ONE LINE FOR A PERSON'S DEAD SEATS (Mission 6, 2026-10-21): every rejoin leaves a seat behind, and the table read
 * as a wall of "offline, never uploaded" headers. Offline seats that NEVER UPLOADED fold into one per name and role,
 * saying how many; a seat that sent lines keeps them, under its own header - an earlier drop's lines may be the bug.
 * The table's order is kept (a Map keeps each key where it first appeared).
 */
function foldOffline(table: SeatDiag[]): ReportSeat[] {
  const folded = new Map<string, ReportSeat>();
  table.forEach((seat, i) => {
    const silent = !seat.online && !seat.diag;
    const key = silent ? `${seat.role}\u0000${seat.name}` : `seat\u0000${i}`;
    const was = folded.get(key);
    folded.set(key, was ? { ...was, seats: was.seats + 1 } : { ...seat, seats: 1 });
  });
  return [...folded.values()];
}

/** A seat's header: who, their role and state (and how many seats it folds), and when it last uploaded. */
function seatHead(seat: ReportSeat): string {
  const when = seat.diagAt ? ` uploaded ${iso(seat.diagAt)}` : ' never uploaded';
  const state = seat.online ? 'online' : seat.seats > 1 ? `offline, ${seat.seats} seats` : 'offline';
  return `--- ${seat.name} (${seat.role}, ${state})${when} ---`;
}

/** The other seats' uploaded lines, each under a header saying who and when; nothing when there are none. */
function tableLines(table: SeatDiag[]): string[] {
  const lines = foldOffline(table).flatMap((seat) => (seat.diag ? [seatHead(seat), seat.diag] : [seatHead(seat)]));
  return lines.length ? ['', '=== TABLE (the other seats, their own last lines) ===', ...lines] : [];
}

export function formatBugReport(input: BugReportInput): string {
  const max = input.maxEntries ?? DEFAULT_MAX_ENTRIES;
  const kept = input.entries.slice(-max);
  const dropped = input.entries.length - kept.length;
  const body = kept.length ? kept.map(line) : ['(no events recorded)'];
  const omitted = dropped > 0 ? [`(${dropped} older entries omitted)`] : [];
  return [
    ...stampLines(input.context, input.env),
    '',
    '=== EVENTS (oldest first) ===',
    ...omitted,
    ...body,
    ...tableLines(input.table ?? []),
  ].join('\n');
}
